import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AdminDashboard from 'layouts/AdminDashboard';
import Container from 'components/Container';
import { getAccountDetail, AdminAccountDTO, AdminBusinessSummaryDTO } from './AccountService';

const fmtDate = (ts: string | null) =>
  ts
    ? new Date(ts).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    : '—';

const SUBSCRIPTION_CHIP: Record<string, { bg: string; color: string; label: string }> = {
  free:             { bg: '#9E9E9E22', color: '#616161', label: 'Free' },
  trialing:         { bg: '#1E88E522', color: '#1565C0', label: 'Trialing' },
  premium:          { bg: '#2E7D3222', color: '#1B5E20', label: 'Premium' },
  expired:          { bg: '#C6282822', color: '#B71C1C', label: 'Expired' },
  cancelled:        { bg: '#C6282822', color: '#B71C1C', label: 'Cancelled' },
  needs_attention:  { bg: '#F9A82522', color: '#E65100', label: 'Needs Attention' },
};

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

const LabelValue = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value ?? '—'}</Typography>
  </Box>
);

const AccountDetailPage = (): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [account, setAccount] = useState<AdminAccountDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    getAccountDetail(Number(id))
      .then((res) => setAccount(res.data))
      .catch(() => setError('Could not load account detail.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <AdminDashboard>
        <Container>
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        </Container>
      </AdminDashboard>
    );
  }

  if (error || !account) {
    return (
      <AdminDashboard>
        <Container>
          <Alert severity="error" sx={{ mt: 3 }}>
            {error || 'Account not found.'}
          </Alert>
        </Container>
      </AdminDashboard>
    );
  }

  const subCfg =
    SUBSCRIPTION_CHIP[account.subscriptionState ?? 'free'] ?? SUBSCRIPTION_CHIP['free'];

  return (
    <AdminDashboard>
      <Box
        sx={{
          background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.06)}, transparent)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/accounts')}
            sx={{ mb: 3 }}
          >
            Accounts
          </Button>

          {/* Section 1: User */}
          <SectionCard title="User">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <LabelValue label="Name" value={`${account.firstName} ${account.lastName}`} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <LabelValue label="Email" value={account.email} />
              </Grid>
              <Grid item xs={12} sm={2}>
                <LabelValue
                  label="Status"
                  value={
                    <Chip
                      label={account.enabled ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        height: 22,
                        bgcolor: account.enabled
                          ? alpha(theme.palette.success.main, 0.12)
                          : alpha(theme.palette.error.main, 0.1),
                        color: account.enabled
                          ? theme.palette.success.dark
                          : theme.palette.error.dark,
                      }}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <LabelValue label="Joined" value={fmtDate(account.createdAt)} />
              </Grid>
            </Grid>
          </SectionCard>

          {/* Section 2: Subscription */}
          <SectionCard title="Subscription">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <LabelValue
                  label="State"
                  value={
                    <Chip
                      label={subCfg.label}
                      size="small"
                      sx={{
                        fontSize: '0.68rem',
                        height: 22,
                        bgcolor: subCfg.bg,
                        color: subCfg.color,
                        fontWeight: 600,
                      }}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <LabelValue
                  label="Tier"
                  value={
                    account.subscriptionTier
                      ? account.subscriptionTier.charAt(0).toUpperCase() +
                        account.subscriptionTier.slice(1)
                      : 'Free'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <LabelValue label="Trial end" value={fmtDate(account.trialEndDate)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <LabelValue label="Renewal date" value={fmtDate(account.renewalDate)} />
              </Grid>
            </Grid>
          </SectionCard>

          {/* Section 3: Household */}
          <SectionCard title="Household">
            {account.hasHousehold ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <LabelValue label="Household" value={account.householdName} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <LabelValue label="Role" value={account.householdRole} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <LabelValue label="Members" value={String(account.householdMemberCount)} />
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No household membership.
              </Typography>
            )}
          </SectionCard>

          {/* Section 4: Business Workspaces */}
          <SectionCard title="Business Workspaces">
            {!account.businesses || account.businesses.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No businesses.
              </Typography>
            ) : (
              <Box>
                {account.businesses.map((b: AdminBusinessSummaryDTO, i: number) => (
                  <React.Fragment key={b.id}>
                    {i > 0 && <Divider sx={{ my: 1.5 }} />}
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" fontWeight={600}>
                          {b.businessName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Created {fmtDate(b.createdAt)} · {b.objectivesCount} objective
                          {b.objectivesCount !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      {b.templateType && (
                        <Chip
                          label={b.templateType.replace(/_/g, ' ')}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: '0.68rem', height: 22 }}
                        />
                      )}
                      <Chip
                        label={b.status}
                        size="small"
                        color={
                          b.status === 'ACTIVE'
                            ? 'success'
                            : b.status === 'ARCHIVED'
                              ? 'error'
                              : 'default'
                        }
                        sx={{ fontSize: '0.68rem', height: 22 }}
                      />
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            )}
          </SectionCard>
        </Container>
      </Box>
    </AdminDashboard>
  );
};

export default AccountDetailPage;
